import {
  ArticleBullets,
  ArticleImage,
  ArticleLayout,
  ArticleParagraph,
  ArticleSection,
  QuickVerdict,
} from '../../../components/marketing/ArticleLayout';
import {
  ARTICLE_IMAGES,
  ARTICLE_SUMMARY_BY_SLUG,
} from '../../../lib/content/articles';

const article = ARTICLE_SUMMARY_BY_SLUG['projectionlab-alternatives-fire'];

export default function ProjectionLabAlternativesFirePage() {
  return (
    <ArticleLayout
      article={article}
      relatedSlugs={['best-fire-planning-apps', 'monarch-money-alternatives-fire']}
    >
      <QuickVerdict
        title="Quick verdict"
        items={[
          'ProjectionLab is a strong fit when deep FIRE modeling is the main job.',
          'Look for an alternative if you want the retirement plan tied more directly to live accounts, budgeting, and day-to-day spending decisions.',
          'Per Diem is the better fit when the question is not only "Can I retire?" but "What can this household spend today while staying on track?"',
        ]}
      />

      <ArticleSection title="Why people look for ProjectionLab alternatives">
        <ArticleParagraph>
          ProjectionLab has earned attention because FIRE planning can get complicated
          quickly. Good long-range planning needs scenarios, assumptions, withdrawal
          logic, inflation, taxes, account treatment, and enough transparency that the
          result feels inspectable instead of magical.
        </ArticleParagraph>
        <ArticleParagraph>
          But not every household starts from a modeling problem. Some start from a
          coordination problem. The plan might be good, but the budget lives somewhere
          else, account balances live somewhere else, transactions need cleanup somewhere
          else, and day-to-day decisions never quite make it back into the FIRE model.
        </ArticleParagraph>
      </ArticleSection>

      <ArticleImage
        source={ARTICLE_IMAGES.mobileScenarios}
        alt="Per Diem mobile scenario planning screen"
        caption="Projection tools are strongest when scenarios are easy to compare without separating them from the household’s live budget and account context."
        variant="phone"
      />

      <ArticleSection title="Best alternatives by use case">
        <ArticleParagraph>
          The best ProjectionLab alternative depends on what you are trying to change.
          A planning-first product, a budget-first product, and a connected household
          workflow solve different problems.
        </ArticleParagraph>
      </ArticleSection>

      <ArticleSection title="1. Per Diem: best for daily spending tied to a FIRE plan">
        <ArticleParagraph>
          Per Diem is built for households that want the long-term plan and the current
          month to share the same source of truth. It connects accounts, transactions,
          budgets, portfolio context, income, taxes, scenarios, and FIRE projections,
          then turns the model into a daily spending number.
        </ArticleParagraph>
        <ArticleBullets
          items={[
            'Best if you want a practical daily answer, not only a projected retirement date.',
            'Best if your budget, accounts, and FIRE assumptions currently live in separate places.',
            'Best if you want demo profiles before connecting real financial accounts.',
          ]}
        />
      </ArticleSection>

      <ArticleSection title="2. Keep ProjectionLab: best for deep standalone modeling">
        <ArticleParagraph>
          If the current pain is modeling depth, ProjectionLab may still be the right
          answer. A focused planning tool can be excellent when the household is willing
          to maintain assumptions and mainly needs to compare long-term scenarios.
        </ArticleParagraph>
        <ArticleParagraph>
          The tradeoff is operational. A strong plan can still become stale if it is not
          connected to current spending, account changes, and the daily decisions that
          create the next version of the plan.
        </ArticleParagraph>
      </ArticleSection>

      <ArticleSection title="3. Monarch Money: best for household budgeting visibility">
        <ArticleParagraph>
          Monarch Money is often a better fit when the immediate problem is shared
          household budgeting, category visibility, and cash-flow organization. It is less
          of a FIRE modeling product, but it can help a household understand current
          spending more clearly.
        </ArticleParagraph>
      </ArticleSection>

      <ArticleSection title="4. Empower: best for broad net worth tracking">
        <ArticleParagraph>
          Empower is a natural option if the main job is account aggregation and net
          worth visibility. It can help answer "where are all the accounts?" more than
          "how should today's spending change the plan?"
        </ArticleParagraph>
      </ArticleSection>

      <ArticleSection title="5. Tiller or a spreadsheet: best for total control">
        <ArticleParagraph>
          Some FIRE households still prefer spreadsheets because they can inspect every
          formula, customize every category, and keep their own assumptions. That control
          is useful, but it comes with maintenance cost.
        </ArticleParagraph>
      </ArticleSection>

      <ArticleImage
        source={ARTICLE_IMAGES.mobileFirePlan}
        alt="Per Diem mobile FIRE plan with projection details"
        caption="Per Diem keeps scenario planning close to the FIRE plan, so a what-if does not become another detached spreadsheet."
        variant="phone"
      />

      <ArticleSection title="How to choose">
        <ArticleParagraph>
          If your spreadsheet or planning tool is working and you only need deeper
          long-range modeling, stay with the tool that gives you confidence. If the plan
          is good but detached from everyday behavior, choose a product that closes that
          gap.
        </ArticleParagraph>
        <ArticleBullets
          items={[
            'Choose a planning-first tool when assumptions and scenario depth matter most.',
            'Choose a budget-first tool when monthly spending visibility is the biggest gap.',
            'Choose Per Diem when you want current spending, live account context, and FIRE planning connected in one workflow.',
          ]}
        />
      </ArticleSection>
    </ArticleLayout>
  );
}
